import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';

export interface IBarChartDataItem {
    value: number;
    name: string;
    color?: string;
    stack?: string;
    seriesName?: string;
}

interface IBarChart {
    data: IBarChartDataItem[] | IBarChartDataItem[][];
    name: string;
    title?: string;
    width?: number | string;
    height?: number | string;
    barWidth?: string;
    unit?: string;
}

export const BarChart = ({
    name,
    title,
    width,
    height,
    data,
    barWidth = '8%',
    unit,
}: IBarChart) => {
    const isStacked = Array.isArray(data[0]);

    let xAxisLabels: string[] = [];
    let series: EChartsOption['series'] = [];

    if (isStacked) {
        // stacked data case
        const stackedData = data as IBarChartDataItem[][];
        xAxisLabels = [...new Set(stackedData[0].map((item) => item.name))];

        const seriesMap: Record<
            string,
            { name: string; stack?: string; data: any[]; color?: string }
        > = {};

        for (const stack of stackedData) {
            for (const item of stack) {
                const key = item.seriesName || name;
                if (!seriesMap[key]) {
                    seriesMap[key] = {
                        name: key,
                        stack: item.stack,
                        data: [],
                        color: item.color,
                    };
                }
                seriesMap[key].data.push({
                    value: item.value,
                    itemStyle: item.color ? { color: item.color } : undefined,
                });
            }
        }

        series = Object.values(seriesMap).map((s) => ({
            type: 'bar',
            name: s.name,
            stack: s.stack,
            barWidth: 6,
            data: s.data,
            emphasis: {
                itemStyle: {
                    opacity: 0.95,
                },
            },
            itemStyle: {
                borderRadius: [16, 16, 0, 0],
            },
        }));
    } else {
        // single series case
        const flatData = data as IBarChartDataItem[];
        xAxisLabels = flatData.map((item) => item.name);
        series = [
            {
                name,
                type: 'bar',
                barWidth: 6,
                data: flatData.map((item) => ({
                    value: item.value,
                    itemStyle: {
                        color: item.color,
                        borderRadius: [16, 16, 0, 0],
                    },
                })),
                emphasis: {
                    itemStyle: {
                        opacity: 0.95,
                    },
                },
            },
        ];
    }

    const option: EChartsOption = {
        color: Array.isArray(data[0])
            ? data[0]
                  .map((item) => item.color)
                  .filter((c): c is string => typeof c === 'string')
            : undefined,
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
            },
            formatter: (params: any) => {
                return params
                    .map((p: any) =>
                        isStacked
                            ? `<div style="display: flex; gap: 4px; align-items: center;">
                                <div style="
                                    width: 12px;
                                    height: 12px;
                                    background-color: ${p.color};
                                    border-radius: 100%;
                                    display: inline-block;
                                "></div>
                                <span style="
                                    font-size: 12px;
                                    font-weight: 400;
                                    color: #4C4C4CCC;
                                ">${p.seriesName}</span>
                                <span style="
                                    font-weight: 500;
                                    font-size: 14px;
                                    margin-left: 4px;
                                ">${p.value} ${unit || ''}</span>
                            </div>`
                            : `${params[0].value} ${unit}`
                    )
                    .join('<br/>');
            },
        },
        xAxis: {
            type: 'category',
            data: xAxisLabels,
            axisTick: {
                alignWithLabel: true,
            },
        },
        yAxis: {
            type: 'value',
            name: unit,
            nameLocation: 'end',
            nameTextStyle: {
                padding: [0, 0, 0, -30],
                fontSize: 14,
            },
        },
        series,
        legend: {
            show: isStacked,
            icon: 'circle',
        },
    };

    return (
        <>
            {title && (
                <h3 className="text-lg font-bold text-navyBlue">{title}</h3>
            )}
            <ReactECharts option={option} style={{ height, width }} />
        </>
    );
};
